/** The History tab's timeline: one merged list of what happened to a record - P15 item 6.
 *
 * System, people and approvals in one place, grouped by day in the reader's zone, with a filter per
 * kind of event and a chip per actor, and a link from each event to the ledger entry that holds it.
 * `recordTimeline.ts` does the merge; this draws it. The revision table stays beneath, because a
 * revision is a record, not an event.
 *
 * **The panel says its scope in one line**, and the line is true to what was read: when this role
 * cannot read the ledger's acts (a Contributor or a Viewer, or an auditor whose engagement narrows the
 * chain), the line says that those are not shown, instead of drawing a shorter timeline that reads as
 * a complete one. A refusal is not an empty day.
 */

import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { useRecordActs } from "../api/activity";
import { useMe } from "../api/auth";
import { useReader } from "../api/profile";
import type { Activity } from "../api/queries";
import { Refused } from "../api/refusal";
import type { Work } from "../api/work";
import { ReadFailed } from "../components/Loaded";
import { EmptyState, InfoPopover, StatusChip } from "../components/ui";
import type { ChipTone } from "../components/ui";
import { instantDate, instantTime } from "../lib/format";
import { EVENT_TYPES, actorsOf, filtered, historyOf } from "./recordTimeline";
import type { EventType, HistoryEvent } from "./recordTimeline";
import { useNameOf } from "./PersonRef";
import { keepSearch } from "./search";
import own from "./RecordHistory.module.css";

const TONE: Readonly<Record<EventType, ChipTone>> = { system: "neutral", human: "info", approval: "ok" };

const TYPE_WORD: Readonly<Record<EventType, string>> = {
  system: "System",
  human: "People",
  approval: "Approval",
};

function toggled<T>(set: ReadonlySet<T>, value: T): Set<T> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

export function RecordTimeline({ activity, work }: { activity: Activity; work: Work | null | undefined }) {
  const reader = useReader();
  const { data: me } = useMe();
  // **Not asked for when the server would refuse it by design** (U7): a Contributor and a Viewer do
  // not read the chain, and an Auditor engaged for a period or for particular companies cannot have
  // it narrowed to the engagement (ADR-0048; scoping it is ADR-0113, proposed). A request made only
  // to be answered 403 is a failed call in every log, and the tab used to say "not shown to your
  // role" beneath a refusal the page had provoked.
  const role = me?.role;
  const mayReadActs =
    (role === "admin" || role === "approver" || role === "auditor") && (me?.engagement ?? null) === null;
  const acts = useRecordActs(activity.activity_record_id, mayReadActs);
  const nameOf = useNameOf();
  const [types, setTypes] = useState<ReadonlySet<EventType>>(new Set());
  const [actors, setActors] = useState<ReadonlySet<string>>(new Set());

  const refused =
    me != null &&
    (!mayReadActs ||
      (acts.error instanceof Refused && (acts.error.status === 403 || acts.error.status === 401)));
  const failed = Boolean(acts.error) && !refused;
  const events = historyOf({
    activity,
    work,
    acts: acts.data?.rows,
    meId: me?.user_id,
    nameOf,
  });
  const shown = filtered(events, types, actors);

  const days = new Map<string, HistoryEvent[]>();
  for (const event of shown) {
    const day = instantDate(event.at, reader);
    days.set(day, [...(days.get(day) ?? []), event]);
  }

  const scope = refused
    ? "Notes, flags, tasks, sign-offs and revisions on this record. The ledger entries behind its figures are on its receipt."
    : "Recorded acts, notes, flags, tasks, sign-offs and revisions on this record.";

  return (
    <section className="card" data-testid="record-timeline">
      <h2 className={own.head}>
        Timeline
        <InfoPopover infoKey="record.history-scope" />
      </h2>
      <p className={own.scope} data-testid="timeline-scope">
        {scope}
      </p>
      {failed && acts.error ? (
        <div className={own.pad}>
          <ReadFailed of="The recorded acts" error={acts.error} onRetry={() => void acts.refetch()} />
        </div>
      ) : null}
      <div className={own.filters} role="group" aria-label="Filter the timeline">
        {EVENT_TYPES.map((entry) => (
          <button
            key={entry.key}
            type="button"
            className={own.chip}
            aria-pressed={types.has(entry.key)}
            data-testid={`timeline-type-${entry.key}`}
            onClick={() => setTypes(toggled(types, entry.key))}
          >
            {entry.label}
          </button>
        ))}
        <span className={own.divider} aria-hidden="true" />
        {actorsOf(events).map((actor) => (
          <button
            key={actor.key}
            type="button"
            className={own.chip}
            aria-pressed={actors.has(actor.key)}
            data-testid={`timeline-actor-${actor.key}`}
            onClick={() => setActors(toggled(actors, actor.key))}
          >
            {actor.label}
          </button>
        ))}
      </div>
      {shown.length === 0 ? (
        <EmptyState
          title={events.length === 0 ? "Nothing has been recorded about this record." : "No events match."}
          compact
        />
      ) : (
        [...days.entries()].map(([day, list]) => (
          <section key={day} className={own.day} aria-label={day} data-testid="timeline-day">
            <h3 className={own.dayHead}>{day}</h3>
            <ul className={own.events}>
              {list.map((event) => (
                <li key={event.key} className={own.event} data-testid="timeline-event" data-type={event.type}>
                  <span className={own.time}>{instantTime(event.at, reader) ?? ""}</span>
                  <StatusChip tone={TONE[event.type]} size="sm">
                    {TYPE_WORD[event.type]}
                  </StatusChip>
                  <span className={own.text}>{event.text}</span>
                  <span className={own.actor}>{event.actor.label}</span>
                  {event.seq === null ? (
                    <span className={own.none} />
                  ) : (
                    <Link
                      to="/ledger/$seq"
                      params={{ seq: String(event.seq) }}
                      search={keepSearch}
                      className={own.seq}
                    >
                      Ledger #{event.seq}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </section>
  );
}
